/**
 * FieldLabelProvider — generic context for tenant-customized field labels.
 *
 * P1.5.4 (2026-04-25). Lifted out of apps/portal/src/branding/FieldLabels.tsx
 * so apps/admin + apps/main + apps/vendor can also wrap content in <T> /
 * useFieldLabel() without each app maintaining its own provider.
 *
 * The provider stays generic — it accepts a flat `labels: Record<key,
 * displayLabel>` map. Each app is responsible for resolving the map from
 * whatever data source it has (PortalMe.tenantSettings.fieldLabels for the
 * portal, AdminMe / Me query for others).
 */
import type { JSX } from 'react';
import { createContext, useContext, useMemo, type ReactNode } from "react";

export type FieldLabelMap = Readonly<Record<string, string>>;

interface FieldLabelContextValue {
  labels: FieldLabelMap;
  /** The tenant's configured display locale, when the host resolves one. */
  locale: string | undefined;
}

const FieldLabelContext = createContext<FieldLabelContextValue>({
  labels: {},
  locale: undefined,
});

export interface FieldLabelProviderProps {
  labels: FieldLabelMap;
  /**
   * BCP-47 locale for tenant-facing date/number formatting, e.g. "en-GB".
   *
   * Optional: omitting it falls back to the runtime default, which is what
   * every consumer did implicitly before. Supplied by the portal from
   * `BrandingProvider`'s `config.locale` (`tenantSettings.defaultLocale`).
   */
  locale?: string;
  children: ReactNode;
}

export function FieldLabelProvider({
  labels,
  locale,
  children,
}: FieldLabelProviderProps): JSX.Element {
  const value = useMemo(() => ({ labels, locale }), [labels, locale]);
  return (
    <FieldLabelContext.Provider value={value}>
      {children}
    </FieldLabelContext.Provider>
  );
}

export function useFieldLabels(): FieldLabelMap {
  return useContext(FieldLabelContext).labels;
}

/** The tenant display locale, or `undefined` for the runtime default. */
export function useTenantLocale(): string | undefined {
  return useContext(FieldLabelContext).locale;
}

/**
 * Resolve a tenant-localised field label at render time.
 *
 *   <T label="orders.order">Order</T>
 *
 * The `children` prop is the English fallback used when the tenant hasn't
 * overridden the label (or no provider is mounted).
 */
export function T({ label, children }: { label: string; children: ReactNode }): JSX.Element {
  const labels = useFieldLabels();
  const overridden = labels[label];
  if (overridden) return <>{overridden}</>;
  return <>{children}</>;
}

export function useFieldLabel(key: string, fallback: string): string {
  return useFieldLabels()[key] ?? fallback;
}
